import { Navigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { canonicalHash } from '@/api/mock/sign';
import { signHashWithFounderKey } from '@/api/real/founderSign';

// ── Types ────────────────────────────────────────────────────────────────────

interface ReportDetailResponse {
  report_id: string; report_type: string;
  period_start: string; period_end: string;
  data_json: Record<string, unknown>;
  narrative_he: string | null; narrative_en: string | null;
  generated_at: string; approved: boolean;
}

interface ActionResponse { id: string; action: string; }

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function approveButtonLabel(approved: boolean): string {
  return approved ? 'Approved' : 'Approve report';
}

function reportTypeHe(t: string): string {
  if (t === 'daily') return 'Daily';
  if (t === 'weekly') return 'Weekly';
  if (t === 'monthly') return 'Monthly';
  return t;
}

// ── Approve flow ──────────────────────────────────────────────────────────────

async function approveWithFounderSig(
  path: string,
  body: { note?: string | null },
): Promise<ActionResponse> {
  const hash = canonicalHash(body);
  const sig = await signHashWithFounderKey(hash);
  return apiFetch<ActionResponse>(path, {
    method: 'POST',
    body,
    headers: { 'X-Founder-Signature': sig },
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8,
      padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
      alignItems: 'flex-start',
    }}>
      <span style={{
        fontSize: '8pt', fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--omega-ink-subtle)', paddingTop: 1,
      }}>
        {label}
      </span>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', wordBreak: 'break-all', fontFamily: 'var(--omega-mono)' }}>
        {children}
      </span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceReportDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { report_id = '' } = useParams<{ report_id: string }>();
  const qc = useQueryClient();

  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = useState('');
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approveSuccess, setApproveSuccess] = useState(false);

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'reports', report_id],
    queryFn: () => apiFetch<ReportDetailResponse>(`/finance/reports/${report_id}`),
    refetchInterval: 30_000,
    enabled: !!report_id,
  });

  const approveMutation = useMutation({
    mutationFn: (approveNote: string | null) =>
      approveWithFounderSig(`/finance/reports/${report_id}/approve`, { note: approveNote }),
    onSuccess: () => {
      setApproveError(null);
      setApproveSuccess(true);
      setShowNoteInput(false);
      setNote('');
      void qc.invalidateQueries({ queryKey: ['finance', 'reports', report_id] });
      void qc.invalidateQueries({ queryKey: ['finance', 'reports', 'list'] });
    },
    onError: (err: Error) => setApproveError(err.message),
  });

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Reports', to: '/omega/finance/reports' },
        { label: report_id.slice(0, 8) },
      ]} />

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', marginTop: 16 }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt', marginTop: 16 }}>Error loading data.</div>}

      {data && (
        <>
          {/* Header */}
          <div style={{ marginTop: 8, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: '13pt', fontWeight: 700, letterSpacing: '0.04em', fontFamily: 'var(--omega-mono)' }}>
                {reportTypeHe(data.report_type)}
              </h1>
              <span style={{
                fontSize: '7.5pt', padding: '1px 6px',
                border: data.approved ? '1px solid rgba(52,211,153,0.5)' : '1px solid rgba(251,191,36,0.5)',
                color: data.approved ? '#34d399' : '#fbbf24',
                letterSpacing: '0.06em', fontWeight: 700, borderRadius: 3,
              }}>
                {data.approved ? '✓ Approved' : 'Pending approval'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 14, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', flexWrap: 'wrap', fontFamily: 'var(--omega-mono)' }}>
              <span>Period: {data.period_start} — {data.period_end}</span>
              <span>Created: {fmtDate(data.generated_at)}</span>
            </div>
          </div>

          {/* Meta fields */}
          <div style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 6, padding: '0 16px', marginBottom: 20,
          }}>
            <FieldRow label="Report ID">{data.report_id}</FieldRow>
            <FieldRow label="Type">{data.report_type}</FieldRow>
            <FieldRow label="Period start">{data.period_start}</FieldRow>
            <FieldRow label="Period end">{data.period_end}</FieldRow>
            <FieldRow label="Created">{fmtDate(data.generated_at)}</FieldRow>
          </div>

          {/* data_json — verbatim pre, no extraction */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              color: 'var(--omega-ink-subtle)', marginBottom: 8,
            }}>
              Data (JSON)
            </div>
            <pre style={{
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 6, padding: '12px 16px', margin: 0,
              fontFamily: 'var(--omega-mono)', fontSize: '8pt', color: 'var(--omega-ink)',
              overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>
              {JSON.stringify(data.data_json, null, 2)}
            </pre>
          </div>

          {/* Narratives — Q-5: verbatim blocks, visually distinct, no number extraction */}
          {data.narrative_he && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'var(--omega-ink-subtle)', marginBottom: 8,
              }}>
                Narrative (Hebrew)
              </div>
              <div style={{
                background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.2)',
                borderRadius: 6, padding: '14px 16px',
                fontSize: '10pt', lineHeight: 1.6, color: 'var(--omega-ink)',
                direction: 'ltr', whiteSpace: 'pre-wrap',
              }}>
                {data.narrative_he}
              </div>
            </div>
          )}

          {data.narrative_en && (
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                color: 'var(--omega-ink-subtle)', marginBottom: 8,
              }}>
                Narrative (EN)
              </div>
              <div style={{
                background: 'rgba(125,211,252,0.04)', border: '1px solid rgba(125,211,252,0.15)',
                borderRadius: 6, padding: '14px 16px',
                fontSize: '10pt', lineHeight: 1.6, color: 'var(--omega-ink)',
                whiteSpace: 'pre-wrap',
              }}>
                {data.narrative_en}
              </div>
            </div>
          )}

          {/* Approve section — only shown when not yet approved */}
          {!data.approved && (
            <div style={{
              position: 'sticky', bottom: 0,
              background: 'var(--omega-bg, #0a0d12)',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              padding: '12px 0 8px', marginTop: 8,
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              {approveError && (
                <div style={{ color: '#f87171', fontSize: '8.5pt' }}>{approveError}</div>
              )}
              {approveSuccess && (
                <div style={{ color: '#34d399', fontSize: '8.5pt' }}>Report approved successfully.</div>
              )}

              {showNoteInput && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Approval note (optional)…"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    style={{
                      flex: 1, background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4,
                      color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
                      fontSize: '9pt', padding: '5px 10px', outline: 'none',
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Escape') { setShowNoteInput(false); setNote(''); }
                      if (e.key === 'Enter') approveMutation.mutate(note || null);
                    }}
                  />
                  <button
                    onClick={() => approveMutation.mutate(note || null)}
                    disabled={approveMutation.isPending}
                    style={{
                      background: '#6ee7b7', color: '#000', border: 'none', borderRadius: 4,
                      fontFamily: 'var(--omega-mono)', fontSize: '9pt', fontWeight: 600,
                      padding: '7px 16px',
                      cursor: approveMutation.isPending ? 'not-allowed' : 'pointer',
                      opacity: approveMutation.isPending ? 0.6 : 1,
                    }}
                  >
                    {approveMutation.isPending ? 'Signing…' : 'Approve'}
                  </button>
                </div>
              )}

              {!showNoteInput && (
                <button
                  onClick={() => setShowNoteInput(true)}
                  style={{
                    background: 'rgba(110,231,183,0.12)', border: '1px solid rgba(110,231,183,0.35)',
                    borderRadius: 4, color: '#6ee7b7',
                    fontFamily: 'var(--omega-mono)', fontSize: '9pt', fontWeight: 600,
                    padding: '7px 18px', cursor: 'pointer', alignSelf: 'flex-start',
                  }}
                >
                  {approveButtonLabel(false)}
                </button>
              )}
            </div>
          )}

          {data.approved && (
            <div style={{
              display: 'inline-block', padding: '8px 14px', borderRadius: 4, marginTop: 8,
              background: 'rgba(52,211,153,0.08)', color: '#34d399',
              border: '1px solid rgba(52,211,153,0.3)',
              fontSize: '8.5pt', letterSpacing: '0.04em',
            }}>
              {approveButtonLabel(true)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
